import { useDate } from 'Globals/useDate'
import { ChartFrame } from 'components/ChartFrame'
import { global_locales, virusnames } from 'components/constants'
import { scaleTime } from 'd3'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useElementSize, useWindowSize } from 'usehooks-ts'
const svgHeight = 600

const locales = global_locales.virussharetable

export const VirusShareHeatmap = ({
  colorMode = 'light',
  lang = 'de',
}: {
  colorMode?: 'dark' | 'light' | undefined
  lang?: 'de' | 'fr'
}) => {
  const selectedwidth = 100
  const selectedspace = 10
  const rowspace = 2
  const labelspace = 100
  const rectheight = svgHeight / 9
  const virusspace = rectheight + rowspace

  const lastvaldate = '2023-10-31'
  const [data, setData] = useState<any | undefined>()
  const [hoveredColumn, setHoveredColumn] = useState<number | undefined>(0)
  const endDate = useDate()
  const [containerWidth, setContainerWidth] = useState<number>(300)
  const [containerRef, { width, height }] = useElementSize()
  const [containerHeight, setContainerHeight] = useState<number>(svgHeight)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(lastvaldate))
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const startdate = isMobile ? new Date('2023-01-01') : new Date('2021-01-01')

  useEffect(() => {
    setContainerWidth(width||100)
  }, [width])
  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard-2023/chart_all_pathogens_share.json'
    )
      .then((a) => a.json())
      .then((a) => {
        var ranks: any = {}
        Object.keys(virusnames).forEach((virus) => {
          a.forEach((d: any) => {
            var sorted = Object.entries(d)
              .filter((f) => f[0] !== 'date' && f[0] !== 'enddate')
              .sort((a: any, b: any) => +b[1] - +a[1])
            var rank = sorted.findIndex((f) => f[0] === virus)
            if (!ranks[virus]) ranks[virus] = []
            ranks[virus].push({
              date: d.date,
              enddate: d.enddate,
              rank: rank,
              virus,
              value: d[virus],
            })
          })
        })

        setData(ranks)
      })
  }, [])



  const drawArea = useMemo(
    () => ({
      top: 40,
      left: labelspace,
      right: containerWidth - 20,
      bottom: containerHeight - 30,
    }),
    [containerWidth, containerHeight, data]
  )

  const x = useMemo(() => {
    return scaleTime()
      .domain([new Date(endDate), new Date(startdate)])
      .range([drawArea.left, drawArea.right])
  }, [drawArea.left, drawArea.right, endDate])

  // six colors from "#000000" to #f8f8f8
  const colors2 = [
    '#000000',
    '#666666',
    '#999999',
    '#bbbbbb',
    '#dddddd',
    '#f0f0f0',
  ]

  // six colors from #D3343F to #378EBD
  let colors = [
    '#691A1F',
    '#9E272F',
    '#D3343F',
    '#DE666F',
    '#E9999F',
    '#F4CCCF',
  ]

  let textcolors = [
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#202346',
    '#202346',
    '#202346',
  ]
  const years: { year: number; x: number }[] = []
  return (
    <ChartFrame
      lang={lang}
      title={locales[lang].title}
      timestamp={
        <>
          {new Date(endDate).toLocaleDateString(lang + '-CH', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </>
      }
      colorKeyEntries={[
        {
          label:
            lang === 'de'
              ? 'Am häufigsten nachgewiesen'
              : 'Le plus souvent détecté',
          color: colors[0],
        },
        { label: '', color: colors[1] },
        { label: '', color: colors[2] },
        { label: '', color: colors[3] },
        { label: '', color: colors[4] },
        {
          label:
            lang === 'de'
              ? 'Am wenigsten nachgewiesen'
              : 'Le moins souvent détecté',
          color: colors[5],
        },
      ]}
      colorKeyIsRange
      source={locales[lang].source}
      colorMode={colorMode}
    >
      <VirusShareHeatmapRoot ref={containerRef}>
        <SVG
          width={'100%'}
          height={svgHeight}
          onMouseLeave={() => setSelectedDate(new Date(lastvaldate))}
          onTouchEndCapture={() => setSelectedDate(new Date(lastvaldate))}
        >
          {data &&
            Object.entries(data)
              .sort(
                (a: any, b: any) =>
                  a[1][a[1].length - 1].rank - b[1][b[1].length - 1].rank
              )
              .map((el, i) => {
                const [virus, virusdata] = el as [string, any[]]
                const datainrange = virusdata
                  .filter(
                    (d) =>
                      new Date(d.date) >= new Date(startdate) &&
                      new Date(d.date) <= new Date(endDate)
                  )
                  .sort((a, b) => +new Date(b.date) - +new Date(a.date))
                const w =
                  (drawArea.right - drawArea.left - selectedwidth) /
                    datainrange.length -
                  0

                return (
                  <g
                    transform={`translate(0,${
                      virusspace * (i + 1) + drawArea.top
                    })`}
                  >
                    <text y={30} textAnchor='end' x={drawArea.left - 15}>
                      {virusnames[virus][lang]}
                    </text>
                    {datainrange.map((d: any, j) => {
                      const isselected =
                        new Date(d.date).toISOString().split('T')[0] ===
                        new Date(selectedDate).toISOString().split('T')[0]

                      const isbeforeselected =
                        +new Date(d.date) > +new Date(selectedDate)

                      const thisyear = d.date.split('-')[0]
                      const lastyear =
                        j > 0 ? datainrange[j - 1].date.split('-')[0] : thisyear

                      if (
                        (lastyear !== thisyear && i === 5) ||
                        (j === 0 && i === 5)
                      ) {
                        years.push({
                          year: thisyear,
                          x:
                            drawArea.left +
                            w * j +
                            (isbeforeselected
                              ? -selectedspace
                              : isselected
                              ? 0
                              : selectedwidth + selectedspace * 0.5) -
                            selectedspace * 0.5,
                        })
                      }

                      return (
                        <g
                          transform={`translate(${
                            drawArea.left +
                            w * j +
                            (isbeforeselected
                              ? -selectedspace
                              : isselected
                              ? 0
                              : selectedwidth + selectedspace * 0.5)
                          },0)`}
                        >
                          <>
                            {lastyear !== thisyear && i === 5 && (
                              <>
                                <line
                                  y1={-virusspace * (i + 1) + drawArea.top}
                                  y2={rectheight * 2}
                                  x1={-selectedspace * 0.5}
                                  x2={-selectedspace * 0.5}
                                  stroke='#fff'
                                  strokeWidth={6}
                                />
                                <line
                                  y1={-10 - virusspace * (i + 1) + drawArea.top}
                                  y2={rectheight * 2}
                                  x1={-selectedspace * 0.5}
                                  x2={-selectedspace * 0.5}
                                  stroke='var(--brandblue-main)'
                                  opacity={0.7}
                                  strokeWidth={1}
                                />
                              </>
                            )}
                          </>
                          {isselected && i === 0 && (
                            <>
                              <text
                                className='date'
                                textAnchor='middle'
                                x={selectedwidth / 2}
                                y={-45}
                              >
                                <tspan>
                                  {' '}
                                  {new Date(d.date).toLocaleDateString(
                                    lang + '-CH',
                                    {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    }
                                  )}
                                  <tspan dy={12} x={selectedwidth / 2}>
                                    &mdash;
                                  </tspan>
                                </tspan>
                                <tspan dy={12} x={selectedwidth / 2}>
                                  {new Date(d.enddate).toLocaleDateString(
                                    lang + '-CH',
                                    {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    }
                                  )}
                                </tspan>
                              </text>
                              <line
                                y1={-drawArea.top - 18}
                                y2={278}
                                x1={-selectedspace * 0.5}
                                x2={-selectedspace * 0.5}
                                stroke='#000'
                              />
                              <line
                                y1={-drawArea.top - 18}
                                y2={278}
                                x1={selectedwidth + selectedspace * 0.5}
                                x2={selectedwidth + selectedspace * 0.5}
                                stroke='#000'
                              />
                            </>
                          )}

                          <rect
                            x={0}
                            y={isselected ? -rowspace * 0.3 : 0}
                            onMouseEnter={() => {
                              setSelectedDate(new Date(d.date))
                            }}
                            onTouchMove={() => {
                              setSelectedDate(new Date(d.date))
                            }}
                            width={(isselected ? selectedwidth : w) + 0.5}
                            height={
                              rectheight + (isselected ? rowspace * 0.6 : 0)
                            }
                            // fill={colors2[1]}
                            opacity={1 - d.rank / 5 + 0.1}
                            fill={colors[d.rank]}
                          />
                          {isselected && (
                            <>
                              <text
                                x={selectedwidth / 2}
                                y={rectheight / 2 + 7}
                                textAnchor='middle'
                                fill={textcolors[d.rank]}
                              >
                                {(d.value + '%').replace('.', ',')}
                              </text>
                            </>
                          )}
                        </g>
                      )
                    })}
                    {years.map(({ year, x }, i) => {
                      const nextx =
                        i < years.length - 1 ? years[i + 1].x : drawArea.right
                      return (
                        <text
                          className='xTick'
                          y={70}
                          x={x + (nextx - x) / 2}
                          textAnchor='middle'
                        >
                          {year}
                        </text>
                      )
                    })}
                  </g>
                )
              })}
        </SVG>
      </VirusShareHeatmapRoot>
    </ChartFrame>
  )
}

const VirusShareHeatmapRoot = styled.div``
const SVG = styled.svg`
  /* background-color: #eeeeee; */
  text {
    font-size: 14px;
    &.date {
      font-size: 12px;
      font-weight: 700;
    }
    &.xTick {
      font-size: 12px;
    }
  }
`
